// Thin wrapper over amazon-cognito-identity-js (SRP auth against the
// sidenerd-frontend user pool; see SideNerdApps terraform/cognito.tf).
//
// Flow: invited users arrive with a temporary password, so first sign-in returns
// the NEW_PASSWORD_REQUIRED challenge — callers must surface a "choose a new
// password" step. Sessions auto-refresh via the refresh token inside getSession().
//
// The DatabaseAPI /user path expects the *ID token* (custom tenancy claims ride
// only in ID tokens), sent raw in the Authorization header — the API Gateway
// COGNITO_USER_POOLS authorizer matches on the token value itself.

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";
import { env } from "../env";

export interface IdTokenClaims {
  email?: string;
  sub?: string;
  "custom:project_string"?: string;
  "custom:client"?: string;
  "custom:role"?: string;
  [claim: string]: unknown;
}

export type SignInResult =
  | { kind: "ok" }
  | { kind: "new_password_required" };

/** True when the build is configured with a user pool (deployed envs). */
export function isAuthConfigured(): boolean {
  return Boolean(env.cognito.userPoolId && env.cognito.clientId);
}

let pool: CognitoUserPool | null = null;
function getPool(): CognitoUserPool {
  if (!pool) {
    if (!isAuthConfigured()) throw new Error("Cognito is not configured");
    pool = new CognitoUserPool({
      UserPoolId: env.cognito.userPoolId!,
      ClientId: env.cognito.clientId!,
    });
  }
  return pool;
}

// User mid-way through the NEW_PASSWORD_REQUIRED challenge (between signIn and
// completeNewPassword). amazon-cognito-identity-js requires the same instance.
let pendingChallengeUser: CognitoUser | null = null;

export function signIn(email: string, password: string): Promise<SignInResult> {
  const user = new CognitoUser({ Username: email, Pool: getPool() });
  const details = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: () => {
        pendingChallengeUser = null;
        resolve({ kind: "ok" });
      },
      onFailure: (err) => reject(err instanceof Error ? err : new Error(String(err))),
      newPasswordRequired: () => {
        pendingChallengeUser = user;
        resolve({ kind: "new_password_required" });
      },
    });
  });
}

export function completeNewPassword(newPassword: string): Promise<void> {
  const user = pendingChallengeUser;
  if (!user) return Promise.reject(new Error("No sign-in in progress"));
  return new Promise((resolve, reject) => {
    // Second arg = attribute updates; tenancy attributes are not writable by this
    // client, so pass none.
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: () => {
        pendingChallengeUser = null;
        resolve();
      },
      onFailure: (err) => reject(err instanceof Error ? err : new Error(String(err))),
    });
  });
}

/** Current session (auto-refreshes if the ID token expired); null if signed out. */
export function getSession(): Promise<CognitoUserSession | null> {
  if (!isAuthConfigured()) return Promise.resolve(null);
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      resolve(err || !session || !session.isValid() ? null : session);
    });
  });
}

export async function getIdToken(): Promise<string | null> {
  const session = await getSession();
  return session ? session.getIdToken().getJwtToken() : null;
}

export async function getClaims(): Promise<IdTokenClaims | null> {
  const session = await getSession();
  return session ? (session.getIdToken().payload as IdTokenClaims) : null;
}

export function signOut(): void {
  pendingChallengeUser = null;
  getPool().getCurrentUser()?.signOut();
}

// ---------------------------------------------------------------------------
// Profile self-service (attributes + password) — used by the Profile page.
// ---------------------------------------------------------------------------

function currentUserOrThrow(): CognitoUser {
  const u = getPool().getCurrentUser();
  if (!u) throw new Error("Not signed in");
  return u;
}

/** Update writable attributes (name, email, picture, custom:default_app). */
export function updateUserAttributes(attrs: Record<string, string>): Promise<void> {
  const user = currentUserOrThrow();
  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null) => {
      if (err) return reject(err);
      const list = Object.entries(attrs).map(([Name, Value]) => ({ Name, Value }));
      user.updateAttributes(list, (e) => (e ? reject(e) : resolve()));
    });
  });
}

/** Change password for the signed-in user. */
export function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  const user = currentUserOrThrow();
  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null) => {
      if (err) return reject(err);
      user.changePassword(oldPassword, newPassword, (e) => (e ? reject(e) : resolve()));
    });
  });
}

/** Confirm a changed email with the verification code Cognito sent. */
export function verifyEmailCode(code: string): Promise<void> {
  const user = currentUserOrThrow();
  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null) => {
      if (err) return reject(err);
      user.verifyAttribute("email", code, {
        onSuccess: () => resolve(),
        onFailure: (e) => reject(e instanceof Error ? e : new Error(String(e))),
      });
    });
  });
}
